import { useState, useRef, useCallback } from "react";
import { yayinTarihiLteNow } from "../../lib/yayinFilter";

export function useChatbotSearch(options) {
	const {
		newsBaseEndpoint = "/api/habers",
		annEndpoint = "/api/duyurulars?populate=*&pagination[pageSize]=2000&sort=createdAt:desc",
		eventsEndpoint = "/api/events?populate=*",
		servicesEndpoint = "/api/hizmetlers?fields=id,slug,adi,arama,icerik,url&populate[fotograf][fields][1]=url&pagination[pageSize]=2000",
		newsLimit = 50,
		apiBase = "",
	} = options || {};

	const [newsResults, setNewsResults] = useState([]);
	const [annResults, setAnnResults] = useState([]);
	const [eventsResults, setEventsResults] = useState([]);
	const [servicesResults, setServicesResults] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	const abortCtrl = useRef(null);

	const extractTextFromRich = (rich) => {
		if (!rich) return "";
		if (typeof rich === "string") return rich;
		if (Array.isArray(rich)) {
			return rich
				.map((node) => {
					if (!node) return "";
					if (typeof node === "string") return node;
					if (node.type === "text") return node.text || "";
					if (node.children) return extractTextFromRich(node.children);
					return "";
				})
				.join(" ");
		}
		if (rich.children) return extractTextFromRich(rich.children);
		return "";
	};

	const pickImageUrl = (imgObj) => {
		if (!imgObj) return null;
		const formats = imgObj.formats || {};
		const mediumUrl = formats.medium && formats.medium.url ? formats.medium.url : null;
		const found = mediumUrl || imgObj.url || null;
		if (!found) return null;
		return found.startsWith("http") ? found : apiBase.replace(/\/$/, "") + found;
	};

	const matchText = (haystack, needle) => {
		if (!needle) return false;
		return haystack.toLocaleLowerCase("tr").includes(needle.toLocaleLowerCase("tr"));
	};

	// Chatbot için özelleştirilmiş haber arama
	const searchNewsForChatbot = async (searchTerm) => {
		try {
			if (!searchTerm) {
				// Boş arama - tüm haberleri getir
				const url = `${newsBaseEndpoint}?fields=baslik,icerik,durum,aciklama,yayin_tarihi,slug&populate[kapak_fotografi][fields][0]=url&sort=yayin_tarihi:desc&pagination[pageSize]=${newsLimit}&${yayinTarihiLteNow()}`;
				const response = await fetch(url, { signal: abortCtrl.current?.signal });
				if (!response.ok) throw new Error("Haber API hatası: " + response.status);
				const data = await response.json();
				return Array.isArray(data.data) ? data.data : [];
			}

			// Arama terimi var - filtrelenmiş arama
			const url = `${newsBaseEndpoint}?filters[$or][0][baslik][$containsi]=${encodeURIComponent(searchTerm)}&filters[$or][1][aciklama][$containsi]=${encodeURIComponent(searchTerm)}&pagination[pageSize]=${newsLimit}&fields=baslik,icerik,durum,aciklama,yayin_tarihi,slug&populate[kapak_fotografi][fields][0]=url&sort=yayin_tarihi:desc&${yayinTarihiLteNow()}`;
			
			const response = await fetch(url, { signal: abortCtrl.current?.signal });
			if (!response.ok) throw new Error("Haber API hatası: " + response.status);
			
			const data = await response.json();
			return Array.isArray(data.data) ? data.data : [];
		} catch (err) {
			console.error('News search error:', err);
			return [];
		}
	};

	// Chatbot için etkinlik arama - API'deki 'events' alanını doğru eşleştir
	const searchEventsForChatbot = async (searchTerm, eventsData) => {
		const matchedEvents = [];
		
		for (const item of eventsData) {
			const name = item.name || "";
			const desc = item.description || [];
			const descText = extractTextFromRich(desc);
			const eventType = item.events || ""; // API'de 'events' alanı var
			const locationName = item.location?.name || "";
			
			// Türkçe etkinlik kelimeleri için genişletilmiş arama
			const searchTerms = [name, descText, eventType, locationName];
			
			// Türkçe etkinlik eşleştirmeleri
			if (searchTerm) {
				const lowerSearchTerm = searchTerm.toLowerCase();
				
				// Etkinlik kelimesi kontrolü - API'deki events alanıyla eşleştir
				if (lowerSearchTerm.includes('etkinlik') || lowerSearchTerm.includes('etkinlikler')) {
					searchTerms.push('etkinlik', 'events', eventType);
				}
				
				// Diğer kelime eşleştirmeleri
				if (lowerSearchTerm.includes('sinema')) {
					searchTerms.push(eventType.toLowerCase().includes('sinema') ? 'sinema' : '');
				}
				if (lowerSearchTerm.includes('film')) {
					searchTerms.push(name.toLowerCase().includes('film') ? 'film' : '');
					searchTerms.push(eventType.toLowerCase().includes('sinema') ? 'film' : '');
				}
				if (lowerSearchTerm.includes('animasyon')) {
					searchTerms.push(name.toLowerCase().includes('animasyon') ? 'animasyon' : '');
				}
				
				// Bugün/yarın kontrolü için
				if (lowerSearchTerm.includes('bugün') || lowerSearchTerm.includes('today')) {
					// Tüm etkinlikleri dahil et - tarih filtrelemesi AI'da yapılacak
					searchTerms.push('bugün', 'today', 'etkinlik', 'events');
				}
				if (lowerSearchTerm.includes('yarın') || lowerSearchTerm.includes('tomorrow')) {
					searchTerms.push('yarın', 'tomorrow', 'etkinlik', 'events');
				}
			}
			
			const hay = searchTerms.join(" ");
			
			// Eğer searchTerm yoksa (boş arama) veya eşleşme varsa dahil et
			if (!searchTerm || matchText(hay, searchTerm) || 
				// Özel durum: etkinlik araması yapılıyorsa tüm etkinlikleri dahil et
				(searchTerm && (searchTerm.toLowerCase().includes('etkinlik') || 
								searchTerm.toLowerCase().includes('bugün') || 
								searchTerm.toLowerCase().includes('yarın') || 
								searchTerm.toLowerCase().includes('hafta')))) {
				
				const img = item.image || null;
				const imgData = img && (img.data || img);
				const imageUrl = pickImageUrl(imgData);
				
				matchedEvents.push({
					id: item.id,
					slug: item.slug || "",
					name,
					imageUrl,
					description: descText,
					events: eventType,
					location: item.location || null,
					create: item.create || null,
					price: item.price || 0,
					quota: item.quota || null,
					ticket: item.ticket || null,
				});
			}
		}
		
		return matchedEvents;
	};

	// Hizmet sonuçlarını işle - fonksiyonu dışarı çıkar
	const processServices = useCallback((servicesData, searchTerm) => {
		const processedServices = [];
		
		for (const item of servicesData) {
			const adi = item.adi || "";
			const aramaFlag = item.arama;
			
			// arama=true VEYA arama=null olan hizmetleri dahil et (arama=false olanları hariç tut)
			if (aramaFlag === false) continue;

			// Siteden kaldırılan hizmetler asistan cevaplarına da girmesin
			// (E-Ruhsat Online İşlemler'den kaldırıldı; CMS kaydı silinene kadar burada filtreli)
			if (item.slug === 'e-ruhsat' || /e-?ruhsat/i.test(adi)) continue;
			
			const icerik = item.icerik || [];
			const text = extractTextFromRich(icerik);
			const url = item.url || null;
			const slug = item.slug || "";
			
			// Arama terimi varsa eşleşme kontrolü yap
			const hay = [adi, text, slug].join(" ");
			
			if (!searchTerm || matchText(hay, searchTerm)) {
				const foto = item.fotograf || null;
				let fotoData = null;
				if (Array.isArray(foto) && foto.length) fotoData = foto[0];
				else fotoData = foto && (foto.data || foto);
				const imageUrl = pickImageUrl(fotoData);
				
				// Hizmet türünü belirle - daha kapsamlı kategoriler
				let serviceType = "Genel Hizmet";
				const lowerAdi = adi.toLowerCase();
				
				if (lowerAdi.includes('imar')) serviceType = "İmar Hizmetleri";
				else if (lowerAdi.includes('vergi') || lowerAdi.includes('ödeme') || lowerAdi.includes('mali')) serviceType = "Mali Hizmetler";
				else if (lowerAdi.includes('evlilik') || lowerAdi.includes('nikah')) serviceType = "Nikah Hizmetleri";
				else if (lowerAdi.includes('ruhsat') || lowerAdi.includes('işyeri') || lowerAdi.includes('iş yeri')) serviceType = "Ruhsat İşlemleri";
				else if (lowerAdi.includes('online') || lowerAdi.includes('elektronik') || url) serviceType = "Online Hizmetler";
				else if (lowerAdi.includes('serbim') || lowerAdi.includes('iletişim') || lowerAdi.includes('talep')) serviceType = "İletişim Hizmetleri";
				else if (lowerAdi.includes('meclis') || lowerAdi.includes('encümen') || lowerAdi.includes('komisyon')) serviceType = "Yönetim Hizmetleri";
				else if (lowerAdi.includes('temizlik') || lowerAdi.includes('çevre') || lowerAdi.includes('veteriner')) serviceType = "Çevre Hizmetleri";
				else if (lowerAdi.includes('fen') || lowerAdi.includes('yapı') || lowerAdi.includes('inşaat')) serviceType = "Teknik Hizmetler";
				else if (lowerAdi.includes('zabıta') || lowerAdi.includes('güvenlik')) serviceType = "Güvenlik Hizmetleri";
				
				processedServices.push({
					id: item.id,
					slug,
					adi,
					imageUrl,
					description: text || 'Hizmet açıklaması mevcut değil',
					url,
					serviceType,
					isOnline: !!url,
					documentId: item.documentId || ""
				});
			}
		}
		
		return processedServices;
	}, []);

	const searchAll = useCallback(async (searchTerm = "") => {
		if (abortCtrl.current) abortCtrl.current.abort();
		abortCtrl.current = new AbortController();
		
		setLoading(true);
		setError(null);
		
		try {
			console.log('Chatbot search - searching for:', searchTerm);

			// Etkinlikler: YAKLAŞAN (bugün ve sonrası) olanları tarihe göre ARTAN çek.
			// Varsayılan endpoint yalnızca ilk 25'i (en eskileri) getirdiğinden gelecek etkinlikler kaçıyordu.
			const startOfToday = new Date();
			startOfToday.setHours(0, 0, 0, 0);
			const eventsUrl = `/api/events?populate=*&filters[create][$gte]=${encodeURIComponent(startOfToday.toISOString())}&sort=create:asc&pagination[pageSize]=50`;

			// Paralel olarak tüm API'leri çağır
			const [newsData, annResp, eventsResp, servicesResp] = await Promise.all([
				searchNewsForChatbot(searchTerm),
				fetch(annEndpoint, { signal: abortCtrl.current.signal }),
				fetch(eventsUrl, { signal: abortCtrl.current.signal }),
				fetch(servicesEndpoint, { signal: abortCtrl.current.signal }),
			]);

			if (!annResp.ok) throw new Error("Duyuru API hatası: " + annResp.status);
			if (!eventsResp.ok) throw new Error("Etkinlik API hatası: " + eventsResp.status);
			if (!servicesResp.ok) throw new Error("Hizmetler API hatası: " + servicesResp.status);

			// Haber sonuçlarını işle
			const processedNews = newsData.map(item => ({
				id: item.id,
				slug: item.slug || "",
				baslik: item.baslik || "",
				aciklama: item.aciklama || "",
				kapakUrl: item.kapak_fotografi?.url ? 
					(item.kapak_fotografi.url.startsWith("http") ? 
						item.kapak_fotografi.url : 
						apiBase.replace(/\/$/, "") + item.kapak_fotografi.url) : null,
				publishedAt: item.yayin_tarihi || item.publishedAt || null,
				durum: item.durum || null,
			}));

			// Duyuru sonuçlarını işle
			const annJson = await annResp.json();
			const annData = Array.isArray(annJson.data) ? annJson.data : [];
			const processedAnnouncements = [];
			
			for (const item of annData) {
				const baslik = item.baslik || "";
				const icerik = item.icerik || [];
				const contentText = extractTextFromRich(icerik);
				const hay = [baslik, contentText].join(" ");
				
				if (!searchTerm || matchText(hay, searchTerm)) {
					processedAnnouncements.push({
						id: item.id,
						baslik,
						duyuru_turu: item.duyuru_turu || null,
						baslangic_tarihi: item.baslangic_tarihi || null,
						icerik: contentText,
					});
				}
			}

			// Etkinlik sonuçlarını işle
			const eventsJson = await eventsResp.json();
			const eventsData = Array.isArray(eventsJson.data) ? eventsJson.data : [];
			const processedEvents = await searchEventsForChatbot(searchTerm, eventsData);

			// Hizmet sonuçlarını işle
			const servicesJson = await servicesResp.json();
			const servicesData = Array.isArray(servicesJson.data) ? servicesJson.data : [];
			const processedServices = processServices(servicesData, searchTerm);
			
			// State'leri senkron olarak güncelle
			setNewsResults(processedNews);
			setAnnResults(processedAnnouncements);
			setEventsResults(processedEvents);
			setServicesResults(processedServices);
			
			console.log('Chatbot search results:', {
				news: processedNews.length,
				announcements: processedAnnouncements.length,
				events: processedEvents.length,
				services: processedServices.length
			});

			return {
				news: processedNews,
				announcements: processedAnnouncements,
				events: processedEvents,
				services: processedServices
			};

		} catch (err) {
			if (err && err.name === "AbortError") {
				// Request aborted - ignore
			} else {
				console.error('Chatbot search error:', err);
				setError(err.message || "Arama sırasında hata oluştu");
			}
			return {
				news: [],
				announcements: [],
				events: [],
				services: []
			};
		} finally {
			setLoading(false);
		}
	}, [newsBaseEndpoint, annEndpoint, eventsEndpoint, servicesEndpoint, newsLimit, apiBase, processServices]);

	// Türkçe öncelikli arama - basitleştirildi
	const searchWithTurkishPriority = useCallback(async (originalTerm) => {
		console.log('Turkish priority search for:', originalTerm);
		
		// Önce orijinal Türkçe terimle ara
		let results = await searchAll(originalTerm);
		
		// Sonuç kontrol et
		let totalResults = (results.news?.length || 0) + 
						  (results.announcements?.length || 0) + 
						  (results.events?.length || 0) + 
						  (results.services?.length || 0);
		
		if (totalResults === 0) {
			console.log('No results with Turkish term, trying related Turkish terms...');
			
			// Türkçe etkinlik ile ilgili terimler
			const turkishEventTerms = ['etkinlik', 'etkinlikler', 'events', 'program', 'aktivite', 'organizasyon'];
			
			for (const term of turkishEventTerms) {
				if (term !== originalTerm.toLowerCase()) {
					console.log('Trying Turkish term:', term);
					results = await searchAll(term);
					
					totalResults = (results.news?.length || 0) + 
								  (results.announcements?.length || 0) + 
								  (results.events?.length || 0) + 
								  (results.services?.length || 0);
					
					if (totalResults > 0) {
						console.log('Found results with Turkish term:', term);
						return results;
					}
				}
			}
			
			// Hala sonuç yoksa İngilizce terimlerle dene
			if (totalResults === 0) {
				console.log('No results with Turkish terms, trying English terms...');
				const englishTerms = ['event', 'events'];
				
				for (const term of englishTerms) {
					console.log('Trying English term:', term);
					results = await searchAll(term);
					
					totalResults = (results.news?.length || 0) + 
								  (results.announcements?.length || 0) + 
								  (results.events?.length || 0) + 
								  (results.services?.length || 0);
					
					if (totalResults > 0) {
						console.log('Found results with English term:', term);
						return results;
					}
				}
			}
			
			// Son çare olarak tüm veriyi getir
			if (totalResults === 0) {
				console.log('Still no results, getting all data...');
				results = await searchAll('');
			}
		}
		
		return results;
	}, [searchAll]);

	// Alternatif arama - eş anlamlı kelimelerle ve API alan adları düzeltildi
	const searchWithSynonyms = useCallback(async (originalTerm) => {
		const synonyms = {
			'etkinlik': ['etkinlikler', 'events', 'program', 'aktivite', 'organizasyon', 'gösterisi', 'event', 'sinema', 'film'],
			'etkinlikler': ['etkinlik', 'events', 'program', 'aktiviteler', 'organizasyonlar', 'gösteriler', 'event', 'sinema', 'film'],
			'bugün': ['today', 'günlük', 'etkinlik', 'etkinlikler', 'events', 'event'],
			'yarın': ['tomorrow', 'ertesi gün', 'etkinlik', 'etkinlikler', 'events', 'event'],
			'hafta': ['week', 'bu hafta', 'haftasonu', 'haftalık', 'etkinlik', 'etkinlikler', 'events', 'event'],
			'sinema': ['film', 'animasyon', 'gösterim', 'movie', 'etkinlik', 'events', 'event'],
			'film': ['sinema', 'gösterim', 'animasyon', 'movie', 'etkinlik', 'events', 'event'],
			'festival': ['fuar', 'şenlik', 'kutlama', 'organizasyon', 'etkinlik', 'events', 'event'],
			'haber': ['haberler', 'duyuru', 'açıklama', 'bilgilendirme'],
			'duyuru': ['haber', 'açıklama', 'bilgilendirme', 'duyurular'],
			'hangi': ['neler', 'ne', 'var', 'mevcut', 'etkinlik', 'etkinlikler', 'events', 'event'],
			'neler': ['hangi', 'ne', 'var', 'mevcut', 'etkinlik', 'etkinlikler', 'events', 'event'],
			'var': ['mevcut', 'neler', 'hangi', 'etkinlik', 'etkinlikler', 'events', 'event'],
			// Hizmet ile ilgili eş anlamlılar - genişletildi
			'hizmet': ['hizmetler', 'service', 'işlem', 'başvuru', 'müracaat', 'belge', 'evrak'],
			'hizmetler': ['hizmet', 'services', 'işlemler', 'başvurular', 'belgeler'],
			'imar': ['imar durumu', 'yapı', 'inşaat', 'ruhsat', 'plan', 'iskân', 'iskan'],
			'vergi': ['ödeme', 'harç', 'tahakkuk', 'borç', 'mali', 'elektronik', 'online'],
			'ruhsat': ['izin', 'belge', 'onay', 'müsaade', 'işyeri', 'iş yeri'],
			'evlilik': ['nikah', 'düğün', 'evlenme', 'işlemler', 'belgeler'],
			'online': ['internet', 'dijital', 'elektronik', 'web', 'e-hizmet'],
			'serbim': ['iletişim', 'talep', 'şikayet', 'istek', 'teşekkür'],
			'meclis': ['kararlar', 'gündem', 'üyeler', 'toplantı'],
			'başkan': ['belediye başkanı', 'yardımcıları', 'yönetim'],
			'müdürlük': ['müdürlükler', 'birim', 'daire', 'servis'],
			'belge': ['evrak', 'işlem', 'başvuru', 'form'],
			'başvuru': ['müracaat', 'talepte bulunma', 'dilekçe'],
			// İngilizce karşılıklar da Türkçe eşleştirme yapabilsin
			'event': ['etkinlik', 'etkinlikler', 'events', 'program', 'aktivite'],
			'events': ['etkinlik', 'etkinlikler', 'event', 'program', 'aktiviteler'],
			'service': ['hizmet', 'hizmetler', 'işlem', 'belge']
		};

		// Türkçe stopwords
		const stopWords = ['bir', 'ile', 'için', 'gibi', 'kadar', 'daha', 'çok', 'az'];
		
		// Orijinal terimi temizle
		const words = originalTerm.toLowerCase()
			.replace(/[.,!?;]/g, ' ')
			.split(/\s+/)
			.filter(word => word.length > 1 && !stopWords.includes(word));

		console.log('Extracted words from query:', words);

		// Tüm kelimelerin eş anlamlılarını topla
		let allSearchTerms = new Set([originalTerm.toLowerCase()]);
		let turkishTerms = new Set();
		let englishTerms = new Set();
		
		words.forEach(word => {
			allSearchTerms.add(word);
			
			// Türkçe kelimeler için özel işlem
			if (['etkinlik', 'etkinlikler', 'hafta', 'bugün', 'yarın', 'hangi', 'neler', 'var', 'hizmet', 'hizmetler'].includes(word)) {
				turkishTerms.add(word);
				turkishTerms.add('events');
			}
			
			// Doğrudan eş anlamlılar
			if (synonyms[word]) {
				synonyms[word].forEach(synonym => {
					allSearchTerms.add(synonym);
					// Türkçe ve İngilizce terimleri ayır
					if (['etkinlik', 'etkinlikler', 'program', 'aktivite', 'hizmet', 'hizmetler'].includes(synonym)) {
						turkishTerms.add(synonym);
						turkishTerms.add('events');
					} else if (['event', 'events'].includes(synonym)) {
						englishTerms.add(synonym);
					}
				});
			}
		});

		// Türkçe terimleri önce dene, sonra İngilizce
		const prioritizedTerms = [...Array.from(turkishTerms), 'events', ...Array.from(englishTerms), ...Array.from(allSearchTerms)];
		const finalSearchTerms = [...new Set(prioritizedTerms)].filter(term => term.length > 1);
		
		console.log('Prioritized search terms (Turkish first):', finalSearchTerms);

		// Önce orijinal terimle ara
		let results = await searchAll(originalTerm);
		
		// Sonuç kontrol et
		let totalResults = (results.news?.length || 0) + 
						  (results.announcements?.length || 0) + 
						  (results.events?.length || 0) + 
						  (results.services?.length || 0);
		
		if (totalResults === 0 && finalSearchTerms.length > 0) {
			console.log('No results with original term, trying prioritized terms...');
			
			// Her terimle sırayla dene (Türkçe öncelikli)
			for (const term of finalSearchTerms) {
				if (term && term.length > 1 && term !== originalTerm.toLowerCase()) {
					console.log('Trying search with prioritized term:', term);
					results = await searchAll(term);
					
					totalResults = (results.news?.length || 0) + 
								  (results.announcements?.length || 0) + 
								  (results.events?.length || 0) + 
								  (results.services?.length || 0);
					
					if (totalResults > 0) {
						console.log('Found results with term:', term);
						return results;
					}
				}
			}
		}
		
		// Hala sonuç yoksa boş arama yap (tüm veriyi getir)
		if (totalResults === 0) {
			console.log('Still no results, getting all data...');
			results = await searchAll('');
		}
		
		return results;
	}, [searchAll]);

	// Sonuçları temizleyen fonksiyon
	const clear = useCallback(() => {
		setNewsResults([]);
		setAnnResults([]);
		setEventsResults([]);
		setServicesResults([]);
		setError(null);
	}, []);

	return {
		results: {
			news: newsResults,
			announcements: annResults,
			events: eventsResults,
			services: servicesResults,
		},
		loading,
		error,
		searchAll,
		searchWithSynonyms,
		searchWithTurkishPriority,
		clear,
	};
}
